'use client'

import dayjs from 'dayjs'
import { Loader2 } from 'lucide-react'
import { useRouter } from 'next/navigation'
import * as React from 'react'
import {
  useCallback,
  useEffect,
  useState,
} from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { last } from 'remeda'

import { useConfirmDialog } from '@/components/confirm-dialog'
import Bell from '@/components/icons/bell.svg'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { getNotificationContent } from '@/constants/notification-type'
import {
  HOUR,
  MINUTE,
} from '@/constants/unit'
import { formatTime } from '@/lib/formatter'
import { cn } from '@/lib/utils'
import {
  getPaginationDisplayType,
  PaginationDisplayType,
} from '@/model/pagination'
import {
  useMarkMineNotificationsAsReadMutation,
  useMarkOneNotificationAsReadMutation,
  useReadAllNotificationsInfiniteQuery,
} from '@/redux/notification'
import { useAppSelector } from '@/redux/utils'

export default function NotificationDropdown() {
  const token = useAppSelector((state) => state.global.token)
  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useReadAllNotificationsInfiniteQuery(undefined, {
    skip: token === undefined,
  })
  const paginationDisplayType = getPaginationDisplayType({
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
  })
  const router = useRouter()

  const [infiniteRef] = useInfiniteScroll({
    loading: isFetchingNextPage,
    hasNextPage,
    onLoadMore: fetchNextPage,
  })
  const unreadCount = last(data?.pages || [])?.unreadCount
  const hasUnreadNotification = unreadCount !== undefined && unreadCount > 0

  const confirm = useConfirmDialog()
  const [markMineNotificationsAsRead] = useMarkMineNotificationsAsReadMutation()
  const markAllAsRead = useCallback(async () => {
    if (!hasUnreadNotification) {
      return
    }
    confirm({
      title: 'Mark All as Read',
      description: 'Are you sure you want to mark all notifications as read?',
      onConfirm: async () => {
        await markMineNotificationsAsRead()
      },
    })
  }, [confirm, hasUnreadNotification, markMineNotificationsAsRead])

  const [markOneNotificationAsRead] = useMarkOneNotificationAsReadMutation()

  const [now, setNow] = useState(new Date())
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date())
    }, MINUTE)
    return () => clearInterval(interval)
  }, [])

  if (!token) return null

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="icon" className="relative size-8 p-1.5">
          <>
            <Bell className="fill-foreground" />
            <div className="absolute size-1.5 top-1 right-1 bg-destructive rounded-full" />
          </>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        sideOffset={8}
        className="w-[25rem] h-[80vh] overflow-hidden flex flex-col border border-white/8 rounded-2xl pt-6 pb-0 px-0 bg-background-secondary"
      >
        <div className="flex justify-between px-6">
          <div className="font-semibold text-sm">
            Notification ({unreadCount || 0})
          </div>
          <div
            className={cn(
              'text-xs',
              hasUnreadNotification ? 'text-primary cursor-pointer' : 'text-disabled-foreground cursor-not-allowed',
            )}
            onClick={markAllAsRead}
          >
            Mark All as Read
          </div>
        </div>
        <Separator className="mt-4 mb-2" />
        <div className="overflow-y-auto">
          {paginationDisplayType === PaginationDisplayType.LoadingInitial && (
            <div className="p-4 flex items-center justify-center">
              <Loader2 className="animate-spin size-4" />
            </div>
          )}
          {paginationDisplayType === PaginationDisplayType.Empty && (
            <div className="flex items-center justify-center p-4 text-muted-foreground text-sm">
              No Notification
            </div>
          )}
          {paginationDisplayType === PaginationDisplayType.Data && data
            ?.pages
            ?.flatMap(({ rows }) => rows)
            .map(({ id, isRead, type, meta, createdAt }) => {
              const { text, href } = getNotificationContent(type, meta)
              return (
                <div
                  key={id}
                  className={cn(
                    'py-4 flex justify-between items-center gap-6 hover:bg-white/8 px-6',
                    href && 'cursor-pointer',
                  )}
                  onClick={() => {
                    if (!isRead) {
                      markOneNotificationAsRead(id)
                    }
                    if (href) {
                      router.push(href)
                    }
                  }}
                >
                  <div className="flex gap-2 overflow-hidden text-sm">
                    {!isRead && <div className="size-2 bg-destructive rounded-full shrink-0 mt-1.5" />}
                    <div>{text}</div>
                  </div>
                  <div className="text-xs text-tertiary-foreground text-nowrap">
                    {now.getTime() - createdAt < 24 * HOUR
                      ? dayjs(createdAt).from(now)
                      : formatTime('MM/DD')(createdAt)}
                  </div>
                </div>
              )
            })}
          {hasNextPage && (
            <div className="p-4 flex items-center justify-center" ref={infiniteRef}>
              <Loader2 className="animate-spin size-4" />
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
